// Define constants for unit graph labels and sections to ensure consistency across the application
export const UNIT_GRAPH_LABELS = Object.freeze({
	measurements: {
		section: 'measurements',
		header: '--- Measurements ---',
		labels: {
			ambient: 'Ambient Temperature',
		},
	},
	stats: {
		section: 'stats',
		header: '--- Statistics ---',
		labels: {
			max: 'String Max',
			avg: 'String Avg',
			min: 'String Min',
		},
	},
	limits: {
		section: 'limits',
		header: '--- Limits ---',
		labels: {
			critHigh: 'Crit High',
			maintHigh: 'Maint High',
			maintLow: 'Maint Low',
			critLow: 'Crit Low',
			initialImpedance: 'Initial Impedance',
		},
	},
});

// Define a sort rank for tooltip labels to control their order in the tooltip
const TOOLTIP_LABEL_SORT_RANK = {
	[UNIT_GRAPH_LABELS.measurements.labels.ambient]: 1,
	[UNIT_GRAPH_LABELS.stats.labels.max]: 10,
	[UNIT_GRAPH_LABELS.stats.labels.avg]: 11,
	[UNIT_GRAPH_LABELS.stats.labels.min]: 12,
	[UNIT_GRAPH_LABELS.limits.labels.critHigh]: 20,
	[UNIT_GRAPH_LABELS.limits.labels.maintHigh]: 21,
	[UNIT_GRAPH_LABELS.limits.labels.maintLow]: 22,
	[UNIT_GRAPH_LABELS.limits.labels.critLow]: 23,
	[UNIT_GRAPH_LABELS.limits.labels.initialImpedance]: 24,
};

// Helper function to get the section header based on the section name
function getSectionHeader(section) {
	return UNIT_GRAPH_LABELS[section]?.header || UNIT_GRAPH_LABELS.measurements.header;
}

// Helper function to determine which section a label belongs to
function getLabelSection(label) {
	if (Object.values(UNIT_GRAPH_LABELS.stats.labels).includes(label)) {
		return UNIT_GRAPH_LABELS.stats.section;
	}

	if (Object.values(UNIT_GRAPH_LABELS.limits.labels).includes(label)) {
		return UNIT_GRAPH_LABELS.limits.section;
	}

	return UNIT_GRAPH_LABELS.measurements.section;
}

// Helper function to format tooltip line text
function formatGraphTooltipLineText(label, formattedValue, unitSuffix) {
	const valueText = formattedValue ?? 'N/A';

	if (!unitSuffix || valueText === 'N/A') {
		return `${label}: ${valueText}`;
	}

	if (typeof valueText === 'string' && valueText.endsWith(` ${unitSuffix}`)) {
		return `${label}: ${valueText}`;
	}

	return `${label}: ${valueText} ${unitSuffix}`;
}

// Use the y-value for tooltip text so scatter-like datasets do not show "(x, y)".
function getTooltipYValueText(context) {
	const yValue = context?.parsed?.y;

	if (yValue === null || yValue === undefined || Number.isNaN(yValue)) {
		return context?.formattedValue;
	}

	const yScale = context?.chart?.scales?.y;
	if (yScale && typeof yScale.getLabelForValue === 'function') {
		return yScale.getLabelForValue(yValue);
	}

	return `${yValue}`;
}

// Function to extract the unit suffix from the y-axis title for tooltip display
function getTooltipUnitSuffix(context) {
	const yTitle = context?.chart?.options?.scales?.y?.title?.text;
	const titleText = Array.isArray(yTitle) ? yTitle.join(' ') : yTitle;

	if (typeof titleText !== 'string' || titleText.length === 0) {
		return '';
	}

	const unitMatch = titleText.match(/\(([^)]+)\)\s*$/);
	if (!unitMatch || !unitMatch[1]) {
		return '';
	}

	return unitMatch[1].trim();
}

// Function to get the sort rank of a tooltip label
function getUnitTooltipSortRank(label) {
	return TOOLTIP_LABEL_SORT_RANK[label] ?? 0;
}

// Function to determine if a section header should be shown before a tooltip item
function getUnitTooltipSectionHeader(context) {
	const tooltipItems = context.chart.tooltip?.dataPoints || [];
	const currentIndex = tooltipItems.findIndex(item =>
		item.datasetIndex === context.datasetIndex && item.dataIndex === context.dataIndex
	);

	if (currentIndex < 0) {
		return null;
	}

	const currentLabel = context.dataset?.label || '';
	const currentGroup = getLabelSection(currentLabel);
	const currentHeader = getSectionHeader(currentGroup);

	if (currentIndex === 0) {
		return currentHeader;
	}

	const prevLabel = tooltipItems[currentIndex - 1]?.dataset?.label || '';
	const prevGroup = getLabelSection(prevLabel);

	if (currentGroup !== prevGroup) {
		return `\n${currentHeader}`;
	}

	return null;
}

// Function to create a custom tooltip configuration for unit graphs
export function createUnitGraphPopover() {

	return {
		filter: function(tooltipItem) {
			return tooltipItem.parsed.y !== null && tooltipItem.parsed.y !== undefined;
		},
		itemSort: (a, b) => {
			const aLabel = a?.dataset?.label || '';
			const bLabel = b?.dataset?.label || '';
			return getUnitTooltipSortRank(aLabel) - getUnitTooltipSortRank(bLabel);
		},
		callbacks: {
			beforeLabel: function(context) {
				return getUnitTooltipSectionHeader(context);
			},
			label: function(context) {
				const datasetLabel = context.dataset.label || '';
				const tooltipValue = getTooltipYValueText(context);
				const unitSuffix = getTooltipUnitSuffix(context);
				return formatGraphTooltipLineText(datasetLabel, tooltipValue, unitSuffix);
			},
		},
		titleFont: {
			weight: 'normal',
		},
		bodyFont: {
			weight: 'normal',
		},
		footerFont: {
			weight: 'normal',
		},
	};
}
